import { useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { recordNotificationEngagement } from '@/lib/notificationEngagement';

interface NativePushState {
  isSupported: boolean;
  isRegistered: boolean;
  permission: 'prompt' | 'granted' | 'denied' | 'unknown';
  loading: boolean;
  lastError?: string;
}

// Helper to extract navigation path from notification data
const getNotificationPath = (data: Record<string, any> | undefined): string => {
  if (!data) return '/app';
  
  const url = data.url || data.data?.url;
  
  if (!url || typeof url !== 'string') return '/app';
  
  try {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const urlObj = new URL(url);
      return urlObj.pathname || '/app';
    }
    if (url.startsWith('/')) {
      return url;
    }
    return `/${url}`;
  } catch {
    return '/app';
  }
};

export const useNativePushNotifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const isNative = Capacitor.isNativePlatform();
  
  // Use ref to always have access to current user in listeners
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  
  const [state, setState] = useState<NativePushState>({
    isSupported: isNative,
    isRegistered: false,
    permission: 'unknown',
    loading: false,
    lastError: undefined,
  });
  
  // Store pending token for retry capability
  const [pendingToken, setPendingToken] = useState<string | null>(null);

  // Ensure listeners are attached before calling PushNotifications.register().
  // (On some builds, calling register before listeners are attached can result in a missed callback.)
  const listenersReadyRef = useRef(false);
  const listenerCleanupRef = useRef<null | (() => void)>(null);

  // If iOS never returns a token callback (common when running on simulator or missing APS entitlement),
  // surface a clear error instead of silently doing nothing.
  const registrationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearRegistrationTimeout = useCallback(() => {
    if (registrationTimeoutRef.current) {
      clearTimeout(registrationTimeoutRef.current);
      registrationTimeoutRef.current = null;
    }
  }, []);

  // Check permission status on mount for native platforms
  useEffect(() => {
    if (isNative) {
      checkPermission();
    }
  }, [isNative]);

  // Check backend for existing APNs registration on mount
  useEffect(() => {
    const checkExistingRegistration = async () => {
      const currentUser = userRef.current;
      if (!isNative || !currentUser) return;

      try {
        console.log('[NativePush] Checking for existing APNs registration...');
        const { data, error } = await supabase
          .from('push_subscriptions')
          .select('id')
          .eq('user_id', currentUser.id)
          .like('endpoint', 'apns://%')
          .eq('is_active', true)
          .limit(1);

        if (error) {
          console.error('[NativePush] Error checking existing registration:', error);
          return;
        }

        if (data && data.length > 0) {
          console.log('[NativePush] Found existing APNs registration, setting isRegistered=true');
          setState(prev => ({ ...prev, isRegistered: true }));
        } else {
          console.log('[NativePush] No existing APNs registration found');
        }
      } catch (error) {
        console.error('[NativePush] Error in checkExistingRegistration:', error);
      }
    };

    checkExistingRegistration();
  }, [isNative, user]);

  const checkPermission = async () => {
    if (!isNative) return;
    
    try {
      console.log('[NativePush] Checking permission status...');
      const result = await PushNotifications.checkPermissions();
      console.log('[NativePush] Permission status:', result.receive);
      setState(prev => ({ 
        ...prev, 
        permission: result.receive as 'prompt' | 'granted' | 'denied',
        isRegistered: prev.isRegistered
      }));
    } catch (error) {
      console.error('[NativePush] Error checking push permissions:', error);
    }
  };

  const requestPermission = async (): Promise<boolean> => {
    if (!isNative) {
      console.log('[NativePush] requestPermission: Not native platform');
      return false;
    }
    
    setState(prev => ({ ...prev, loading: true }));
    
    try {
      console.log('[NativePush] Requesting permission from system...');
      const result = await PushNotifications.requestPermissions();
      const granted = result.receive === 'granted';
      console.log('[NativePush] Permission result:', result.receive, 'granted:', granted);
      
      setState(prev => ({ 
        ...prev, 
        permission: result.receive as 'prompt' | 'granted' | 'denied',
        loading: false,
        lastError: undefined,
      }));
      
      return granted;
    } catch (error) {
      console.error('[NativePush] Error requesting push permissions:', error);
      setState(prev => ({ ...prev, loading: false, lastError: 'Permission request failed' }));
      return false;
    }
  };

  // Function to send token to backend
  const sendTokenToBackend = useCallback(async (token: string) => {
    const currentUser = userRef.current;
    const platform = Capacitor.getPlatform();
    
    console.log('[NativePush] sendTokenToBackend called', { 
      hasUser: !!currentUser, 
      userId: currentUser?.id,
      tokenLength: token.length,
      platform
    });

    // Validate APNs token format before sending (iOS tokens should be exactly 64 hex chars)
    const isValidApnsToken = /^[A-Fa-f0-9]{64}$/.test(token);
    if (platform === 'ios' && !isValidApnsToken) {
      console.error('[NativePush] Invalid APNs token format:', {
        length: token.length,
        expected: 64,
        tokenPreview: token.substring(0, 20) + '...'
      });
      setState(prev => ({
        ...prev,
        loading: false,
        lastError: `Invalid APNs token: got ${token.length} chars, expected 64 hex characters`,
      }));
      return false;
    }
    
    if (!currentUser) {
      console.log('[NativePush] No user available, storing token for later');
      setPendingToken(token);
      return false;
    }
    
    try {
      const platform = Capacitor.getPlatform();
      console.log('[NativePush] Invoking register-native-push-token edge function...', {
        platform,
        tokenPrefix: token.substring(0, 20) + '...'
      });
      
      const { data, error } = await supabase.functions.invoke('register-native-push-token', {
        body: { token, platform },
      });

      console.log('[NativePush] Edge function response:', { data, error });

      if (error) {
        console.error('[NativePush] Edge function error:', error);
        console.error('[NativePush] Error details:', JSON.stringify(error));
        throw error;
      }
      
      if (!data?.ok) {
        console.error('[NativePush] Edge function returned failure:', data);
        throw new Error(data?.error || 'Token registration failed');
      }
      
      console.log('[NativePush] Token successfully registered!');
      setPendingToken(null);
      setState(prev => ({ ...prev, isRegistered: true, loading: false, lastError: undefined }));
      return true;
    } catch (error) {
      console.error('[NativePush] Error storing push token:', error);
      setPendingToken(token); // Store for retry
      setState(prev => ({
        ...prev,
        loading: false,
        isRegistered: false,
        lastError: (error as any)?.message || 'Failed to save push token',
      }));
      return false;
    }
  }, []);

  // Retry sending pending token when user becomes available
  useEffect(() => {
    if (pendingToken && user) {
      console.log('[NativePush] User now available, retrying pending token registration');
      sendTokenToBackend(pendingToken);
    }
  }, [pendingToken, user, sendTokenToBackend]);

  const register = useCallback(async () => {
    console.log('[NativePush] register() called', { 
      isNative, 
      hasUser: !!user,
      userId: user?.id,
      currentPermission: state.permission 
    });
    
    if (!isNative) {
      console.log('[NativePush] Skipping - not native platform');
      return;
    }
    
    if (!user) {
      console.log('[NativePush] Skipping - no user logged in');
      return;
    }
    
    setState(prev => ({ ...prev, loading: true, lastError: undefined }));
    
    try {
      // Attach listeners *before* we request/register.
      // This is defensive: listeners are also attached in the effect below,
      // but this guarantees we never miss a fast registration callback.
      if (!listenersReadyRef.current) {
        console.log('[NativePush] Attaching push listeners (defensive setup)');
        listenersReadyRef.current = true;

        const registrationListener = PushNotifications.addListener('registration', async (token: Token) => {
          console.log('[NativePush] Registration callback received!');
          console.log('[NativePush] Token:', token.value.substring(0, 30) + '...');

          clearRegistrationTimeout();

          await sendTokenToBackend(token.value);
        });

        const errorListener = PushNotifications.addListener('registrationError', (error) => {
          console.error('[NativePush] Registration error callback:', error);
          console.error('[NativePush] Error details:', JSON.stringify(error));

          clearRegistrationTimeout();

          setState(prev => ({
            ...prev,
            loading: false,
            isRegistered: false,
            lastError: (error as any)?.message || 'Native registration error',
          }));
        });

        const receivedListener = PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
          console.log('[NativePush] Push notification received in foreground:', JSON.stringify(notification));
        });

        const actionListener = PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
          console.log('[NativePush] Push notification action performed:', JSON.stringify(action));

          const path = getNotificationPath(action.notification.data);
          console.log('[NativePush] Navigating to path:', path);

          navigate(path);
        });

        listenerCleanupRef.current = () => {
          console.log('[NativePush] Cleaning up push notification listeners (defensive cleanup)');
          clearRegistrationTimeout();
          registrationListener.then(l => l.remove());
          errorListener.then(l => l.remove());
          receivedListener.then(l => l.remove());
          actionListener.then(l => l.remove());
        };
      }

      console.log('[NativePush] Requesting permission...');
      const hasPermission = await requestPermission();
      console.log('[NativePush] Permission result:', hasPermission);
      
      if (!hasPermission) {
        console.log('[NativePush] Permission denied, aborting registration');
        setState(prev => ({ ...prev, loading: false, lastError: 'Permission denied' }));
        return;
      }

      console.log('[NativePush] Calling PushNotifications.register()...');
      await PushNotifications.register();
      console.log('[NativePush] PushNotifications.register() completed, waiting for token callback...');

      clearRegistrationTimeout();
      registrationTimeoutRef.current = setTimeout(() => {
        console.error('[NativePush] Timed out waiting for APNs token callback');
        setState(prev => ({
          ...prev,
          loading: false,
          isRegistered: false,
          lastError:
            'Timed out waiting for an APNs token callback. Apple did not return a device token. Common causes: running on a simulator, signing/provisioning mismatch, or missing Push Notifications / aps-environment entitlement in the built app.',
        }));
      }, 12000);
      
    } catch (error) {
      console.error('[NativePush] Registration error:', error);
      clearRegistrationTimeout();
      setState(prev => ({ ...prev, loading: false, lastError: 'Native registration failed: ' + (error as any)?.message }));
    }
  }, [isNative, user, state.permission, clearRegistrationTimeout]);

  // Set up listeners for push events
  useEffect(() => {
    if (!isNative) return;

    console.log('[NativePush] Setting up push notification listeners');

    // If register() already attached listeners defensively, don't attach a second set.
    if (listenersReadyRef.current) {
      console.log('[NativePush] Listeners already attached (skipping effect setup)');
      return () => {
        // Cleanup happens in the defensive cleanup.
        listenerCleanupRef.current?.();
        listenerCleanupRef.current = null;
        listenersReadyRef.current = false;
      };
    }

    // Mark listeners as attached (so register() won't attach a duplicate set later)
    listenersReadyRef.current = true;

    // On successful registration with APNs/FCM
    const registrationListener = PushNotifications.addListener('registration', async (token: Token) => {
      console.log('[NativePush] Registration callback received!');
      console.log('[NativePush] Token:', token.value.substring(0, 30) + '...');

      clearRegistrationTimeout();

      await sendTokenToBackend(token.value);
    });

    // On registration error
    const errorListener = PushNotifications.addListener('registrationError', (error) => {
      console.error('[NativePush] Registration error callback:', error);
      console.error('[NativePush] Error details:', JSON.stringify(error));

      clearRegistrationTimeout();

      setState(prev => ({
        ...prev,
        loading: false,
        isRegistered: false,
        lastError: (error as any)?.message || 'Native registration error',
      }));
    });

    // Handle received push notification when app is in foreground
    const receivedListener = PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      console.log('[NativePush] Push notification received in foreground:', JSON.stringify(notification));
    });

    // Handle notification action (user tapped on notification)
    const actionListener = PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
      console.log('[NativePush] Push notification action performed:', JSON.stringify(action));
      
      const data = action.notification.data;
      void recordNotificationEngagement('opened', data?.notificationId ?? data?.data?.notificationId, data?.proactiveSendId ?? data?.data?.proactiveSendId);
      const path = getNotificationPath(data);
      console.log('[NativePush] Navigating to path:', path);
      
      navigate(path);
    });

    listenerCleanupRef.current = () => {
      console.log('[NativePush] Cleaning up push notification listeners');
      clearRegistrationTimeout();
      registrationListener.then(l => l.remove());
      errorListener.then(l => l.remove());
      receivedListener.then(l => l.remove());
      actionListener.then(l => l.remove());
    };

    return () => {
      listenerCleanupRef.current?.();
      listenerCleanupRef.current = null;
      listenersReadyRef.current = false;
    };
  }, [isNative, navigate, sendTokenToBackend, clearRegistrationTimeout]);

  // When permission was already granted (e.g. on a previous account or install),
  // silently re-register this phone for the currently signed-in account.
  // No OS prompt is shown, so this doesn't bypass the prompt coordinator.
  const autoRegisteredFor = useRef<string | null>(null);
  useEffect(() => {
    if (!isNative || !user || state.permission !== 'granted') return;
    if (autoRegisteredFor.current === user.id) return;
    autoRegisteredFor.current = user.id;
    void PushNotifications.register().catch((e) =>
      console.error('[NativePush] Silent re-register failed:', e)
    );
  }, [isNative, user, state.permission]);

  const unregister = async () => {
    if (!isNative || !user) return;
    
    console.log('[NativePush] Unregistering push notifications');
    setState(prev => ({ ...prev, loading: true, lastError: undefined }));
    
    try {
      await supabase
        .from('push_subscriptions')
        .update({ is_active: false })
        .eq('user_id', user.id)
        .like('endpoint', 'apns://%');
      
      console.log('[NativePush] Unregistration complete');
      setState(prev => ({ ...prev, isRegistered: false, loading: false }));
    } catch (error) {
      console.error('[NativePush] Error unregistering push notifications:', error);
      setState(prev => ({ ...prev, loading: false, lastError: 'Failed to unregister' }));
    }
  };

  // Manual retry function for debugging
  const retryRegistration = useCallback(async () => {
    console.log('[NativePush] Manual retry triggered');
    if (pendingToken) {
      console.log('[NativePush] Retrying with pending token');
      await sendTokenToBackend(pendingToken);
    } else {
      console.log('[NativePush] No pending token, calling full register flow');
      await register();
    }
  }, [pendingToken, sendTokenToBackend, register]);

  return {
    isNative,
    isSupported: state.isSupported,
    isRegistered: state.isRegistered,
    permission: state.permission,
    loading: state.loading,
    lastError: state.lastError,
    pendingToken: !!pendingToken,
    register,
    unregister,
    requestPermission,
    retryRegistration,
  };
};
